/**
 * Jubly Calendar Agenda/Schedule View — Google Calendar style
 * Chronological list of upcoming events grouped by date
 */

import { useMemo } from 'react';
import { MapPin, Clock } from 'lucide-react';

export default function AgendaView({
  currentDate,
  events,
  calendars,
  onEventClick
}) {
  // Group events by date, sorted chronologically
  const groupedEvents = useMemo(() => {
    const sorted = [...events].sort((a, b) =>
      new Date(a.start_time) - new Date(b.start_time)
    );

    const groups = [];
    let currentGroup = null;

    for (const event of sorted) {
      const eventDate = new Date(event.start_time);
      const dateKey = `${eventDate.getFullYear()}-${eventDate.getMonth()}-${eventDate.getDate()}`;

      if (!currentGroup || currentGroup.key !== dateKey) {
        currentGroup = {
          key: dateKey,
          date: new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate()),
          events: []
        };
        groups.push(currentGroup);
      }
      currentGroup.events.push(event);
    }

    return groups;
  }, [events]);

  const today = new Date();
  const isToday = (date) =>
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  const formatTime = (dateStr) =>
    new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

  const getColor = (event) => event.calendar_color || event.color || '#3B82F6';

  if (groupedEvents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400">
        <Clock className="w-16 h-16 mb-4 stroke-1" />
        <p className="text-lg font-medium text-gray-500">No upcoming events</p>
        <p className="text-sm mt-1">Events you create will show up here</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-3xl mx-auto py-4">
        {groupedEvents.map((group) => (
          <div key={group.key} className="flex">
            {/* Date column */}
            <div className="w-[88px] flex-shrink-0 pt-4 pr-4 text-right">
              <div className={`text-[11px] font-medium tracking-wider ${isToday(group.date) ? 'text-blue-600' : 'text-gray-500'}`}>
                {group.date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}
              </div>
              <div className={`
                text-[26px] font-normal leading-tight
                ${isToday(group.date) ? 'text-blue-600' : 'text-gray-800'}
              `}>
                {group.date.getDate()}
              </div>
              {(group.date.getDate() === 1 || group === groupedEvents[0]) && (
                <div className="text-[11px] text-gray-400 mt-0.5">
                  {group.date.toLocaleDateString('en-US', { month: 'short' })}
                </div>
              )}
            </div>

            {/* Events column */}
            <div className="flex-1 border-l border-gray-200 pl-4 pb-6">
              {group.events.map((event) => {
                const color = getColor(event);
                return (
                  <button
                    key={event.id}
                    onClick={() => onEventClick?.(event)}
                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors flex gap-3 group"
                  >
                    {/* Color bar */}
                    <div
                      className="w-1 rounded-full flex-shrink-0 self-stretch min-h-[40px]"
                      style={{ backgroundColor: color }}
                    />

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {event.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {event.is_all_day ? (
                          'All day'
                        ) : (
                          `${formatTime(event.start_time)} – ${formatTime(event.end_time)}`
                        )}
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
